<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapGetters } from 'vuex'

import axios from 'axios'
import uniqby from 'lodash.uniqby'

import ComponentAveragesTable from '@/components/ComponentAveragesTable';
import ViewFilters from '@/components/ViewFilters'
import SchoolAverages from '@/components/admin/SchoolAverages'
import SelfReflection from '@/components/SelfReflection'
import DashboardHeading from '@/components/DashboardHeading'

import ComponentDistribution from '@/components/ComponentDistribution'

import { schoolDashboardHelper } from '@/helpers/schoolDashboardHelper'
import { onboardingHelper } from '@/helpers/onboardingHelper'

import EventBus from '@/event-bus'
import store from '@/store'
import { Watch } from 'vue-property-decorator'

import format from 'date-fns/format'

import pickby from "lodash.pickby"
import startswith from "lodash.startswith"

import FuzzySearchbox from 'fuzzy-searchbox'

const apiBase = API_APP_PATH + "/v1"

@Component({
    components: {
      ViewFilters,
      ComponentAveragesTable,
      SchoolAverages,
      SelfReflection,
      DashboardHeading,
      ComponentDistribution,
      FuzzySearchbox
    },
    computed: {
      ...mapGetters({
        userRole: "getUserRole",
        id: "getEmployeeId",
        fiscalYear: "getFiscalYear",
        schools: "admin/getSchools",
        selectedSchool: "admin/getSelectedSchool"
      }),
      fetchedSchool() {
        return this.schools.find(x => x.SchoolDBN === this.selectedSchool.dbn)
      },
      hasObservations () {
        const total = this.selectedSchool.teachers
          .filter(x => x.FiscalYear === this.selectedYear)
          .map(x => x.observation_N)
          .reduce((a,b) => {
            if (b) return a + b
            return a
          }, 0)
          return total > 0
      },
      evaluators() {
        const { teachers } = this.selectedSchool
        const listForSelectedSchoolYear = teachers.filter(x => x.FiscalYear === this.selectedYear)
        const list = listForSelectedSchoolYear
          .map(x => x.EvaluatorName ? x.EvaluatorName.replace(/(^\s+|\s+$)/g,'') : null)
          .filter(x=> x)
          .sort((a,b) => {
            var nameA = a.split('|')[0].split(' ')[1]  
            var nameB = b.split('|')[0].split(' ')[1]
            if (nameA < nameB) {
              return -1
            }
            if (nameA > nameB) {
              return 1
            }
            // names must be equal
            return 0
          })
        return [...new Set(list)]
      },
      grades() {
        const { teachers } = this.selectedSchool
        const listForSelectedSchoolYear = teachers.filter(x => x.FiscalYear === this.selectedYear)
        const list = listForSelectedSchoolYear.map(x => x.GRADE_LEVEL_DESC).filter(x => x)
        return [...new Set(list)].sort()
      },
      subjects() {
        const { teachers } = this.selectedSchool
        const listForSelectedSchoolYear = teachers.filter(x => x.FiscalYear === this.selectedYear)
        const list = listForSelectedSchoolYear.map(x => x.SUBJECT_DESC).filter(x => x)
        return [...new Set(list)].sort()
      },
      schoolDataDisplayed() {
        return this.selectedSchool.motp_data ? this.selectedSchool.motp_data.find(data => data.FiscalYear === this.selectedYear) : false
      },
      schoolYears () {
        if (this.selectedSchool) {
          const { teachers } = this.selectedSchool
          const years = Array.from(new Set(teachers.map(x => x.FiscalYear)))
          return years.sort(function(a, b) {
            return b - a
          })
        }
        // what to return here?
        return []
      },
      areFiltersApplied () {
        const { evalFilters, subjectFilters, gradeFilters } = this
        return evalFilters.length > 0 || subjectFilters.length > 0 || gradeFilters.length > 0
      },
      filteredSchoolAverages() {
        if (!this.areFiltersApplied) {
          return null
        } else {

          const teachers = this.listFilteredBySelectedFilters

          let collection = {
            'component1a_mean': [],
            'component1e_mean': [],
            'component2a_mean': [],
            'component2d_mean': [],
            'component3b_mean': [],
            'component3c_mean': [],
            'component3d_mean': [],
            'component4e_mean': []
          }

          teachers.forEach(x => {
            collection['component1a_mean'].push(x.component1a_mean)
            collection['component1e_mean'].push(x.component1e_mean)
            collection['component2a_mean'].push(x.component2a_mean)
            collection['component2d_mean'].push(x.component2d_mean)
            collection['component3b_mean'].push(x.component3b_mean)
            collection['component3c_mean'].push(x.component3c_mean)
            collection['component3d_mean'].push(x.component3d_mean) 
            collection['component4e_mean'].push(x.component4e_mean)
          })

          for (let mean in collection) {
            collection[mean] = collection[mean].filter(x => x !== null)
            collection[mean] = collection[mean].reduce((a,b) => a+b, 0) / collection[mean].length
          }
          return collection
        }
      },
      renderedChartData () {
        const list = this.areFiltersApplied ? this.listFilteredBySelectedFilters : this.listFilteredBySelectedSchoolYear
        
        const manipulated = list.map(t => {
          const r = /mean/

          let data = {}

          data.HEDI_Rating = t.SL_MOTP_Rating
          data.FiscalYear = t.FiscalYear

          for (let prop in t) {
            if (r.test(prop)) {
              const key = `Avg_${prop.split('_')[0].slice(-2)}`
              data[key] = t[prop]
            }
          }
          return data
        }) 

        return manipulated.map(this.addAverages).map(t => {
          t.HEDI_Rating = {value: t.HEDI_Rating, level: this.hediLevel(t.HEDI_Rating)}
          return t
        })
      },
      listFilteredBySelectedSchoolYear () {
        const { selectedYear, selectedSchool } = this
        return selectedSchool.teachers.filter(x=> x.FiscalYear === selectedYear)
      },
      listFilteredBySelectedFilters () {
        let { listFilteredBySelectedSchoolYear, evalFilters, gradeFilters, subjectFilters, checkEvalFilter, checkGradeFilter, checkSubjectFilter, tipStatus } = this
        let teachers = []
        listFilteredBySelectedSchoolYear.forEach(teacher => {
          const { EvaluatorName, GRADE_LEVEL_DESC, SUBJECT_DESC } = teacher
          if (checkEvalFilter(EvaluatorName, evalFilters) && checkGradeFilter(GRADE_LEVEL_DESC, gradeFilters) && checkSubjectFilter(SUBJECT_DESC, subjectFilters)) {
            teachers.push(teacher)
          }
        })
        // need to remove whitespace until db is cleaned
        teachers = teachers.map(x => {
          x.EvaluatorName = x.EvaluatorName ? x.EvaluatorName.replace(/(^\s+|\s+$)/g,'') : null
          return x
        })
        return tipStatus ? teachers.filter(x => x.flag_TIP) : teachers
      },
      renderedTeacherList () {
        return this.areFiltersApplied ? this.listFilteredBySelectedFilters : this.listFilteredBySelectedSchoolYear
      }
    }
})

export default class Dashboard extends Vue {

  selectedYear = null

  format = format
  loading = false
  tipStatus = false
  dialogVisible = false
  modalInfo = null
  selectedComments = ''
  steps = schoolDashboardHelper().steps
  
  school = null

  evalFilters = []
  subjectFilters = []
  gradeFilters = []

  displayEvaluators = false
  displaySubjects = false
  displayGrades = false

  componentMeans = [
    'component1a_mean',
    'component1e_mean',
    'component2a_mean',
    'component2d_mean',
    'component3b_mean',
    'component3c_mean',
    'component3d_mean',
    'component4e_mean'
  ]

  @Watch('school')
  async onSchoolSelected (val, oldVal) {

    this.clearFilters()

    this.loading = true
    await store.dispatch('admin/fetchSelectedSchool', {dbn: val.SchoolDBN})
    this.loading = false
  }

  @Watch('selectedYear')
  onYearSelected (val, oldVal) {
    this.clearFilters()
  }

  created () {
    store.commit('currentPage', 'school')
  }
  async mounted () {

    let { userRole, fiscalYear } = this

    this.selectedYear = fiscalYear
    
    // central level users who land on this page already have a school selected
    if (userRole === 'Principal' || userRole === 'Assistant Principal' || userRole === 'School Based Staff') {
      const result = await axios.get(`${apiBase}/data/admin/app/ge/info/schools/${this.id}`)
      await store.dispatch('admin/fetchSelectedSchool', {dbn: result.data[0].SchoolDBN})
    }
    
    EventBus.$emit('entering admin dashboard')
    EventBus.$on('loading', () => {
      this.loading = true
    })
    EventBus.$on('teacherComments', data => {
      this.modalInfo = data
      this.selectedComments = data.teacher.components[data.component].comments.filter(c => c.FiscalYear === this.selectedYear && c.Rating > 0)
      this.loading = false
      this.dialogVisible = true
    })
    EventBus.$on('start intro', () => {
      this.startIntro()
    })
    EventBus.$on('view_observations', () => {
      introJs().exit()
    })
    this.$nextTick(() => {
      if (localStorage.getItem('doneAdminSchoolDashOnboarding')) {
        return
      } else {
        setTimeout(() => {
          this.startIntro();
        }, 1000)
      }
    })
  }
  uniqListOfSchools (schools) {
    return uniqby(schools, 'SchoolDBN')
  }
  uniqListOfTeachers (teachers) {
    return uniqby(teachers, 'EmployeeID')
  }
  uniqEvaluators (values) {
    // need to remove whitespace until db is cleaned
    let list = values.map(x=> x.replace(/(^\s+|\s+$)/g,''))
    return [...new Set(list)]
  }  
  hediLevel(value) {
    switch (value) {
      case 'Highly Effective':
        return 4
      case 'Effective':
        return 3
      case 'Developing':
        return 2
      case 'Ineffective':
        return 1
    }
  }

  getAverages(teacher) {
    return pickby(teacher, function(value, key) {
      return startswith(key, "Avg")
    })
  }

  addAverages(teacher) {
    const averages = this.getAverages(teacher)
    let arr = []
    let t = {}
    for (let prop in averages) {
      if (teacher[prop] === averages[prop]){
        t[prop] = {
          value: averages[prop],
          hedi: this.assignHedi(averages[prop])
        }
        arr.push(t[prop])
      }
    }
    teacher.averages = arr
    return teacher
  }

  assignHedi(rating) {
    if (rating >= 1.00 && rating <= 1.74) {
      return "ineffective";
    } else if (rating >= 1.75 && rating <=2.50) {
      return "developing";
    } else if (rating >= 2.51 && rating <= 3.50) {
      return "effective";
    } else if (rating >= 3.51 && rating <= 4.00) {
      return "highly-effective"
    }
  }

  displayStringForSchool (s) {
    return `${s.dbn || s.SchoolDBN} - ${s.name || s.SchoolDBN_Name}`
  }
  checkEvalFilter (val, arr) {
    return arr.length === 0 || arr.indexOf(val) !== -1
  }
  checkGradeFilter (val, arr) {
    return arr.length === 0 || arr.indexOf(val) !== -1
  }
  checkSubjectFilter (val, arr) {
    return arr.length === 0 || arr.indexOf(val) !== -1
  }
  
  hideEvaluators () {
    this.displayEvaluators = false
  }
  hideGrades () {
    this.displayGrades = false
  }
  hideSubjects () {
    this.displaySubjects = false
  }
  clearFilters () {
    this.evalFilters = []
    this.subjectFilters = []
    this.gradeFilters = []
  }

  async startIntro() {
    await onboardingHelper().startIntro(
      introJs(), this, 'doneAdminSchoolDashOnboarding'
    )
    if (document.getElementsByClassName('caseload').length === 0) {
      const caseloadButton = schoolDashboardHelper().createElement(
        schoolDashboardHelper().caseloadButton(introJs(), this)
      )
      document.querySelector('.introjs-tooltipbuttons')
              .insertBefore(
                caseloadButton, document.querySelector('.introjs-nextbutton')
              )
    }
  }
}  

</script>

<template>
  <div>
    <div 
      class="component-average"
    > 
      <el-dialog :visible.sync="dialogVisible" v-if="modalInfo" width="35%">
      <div class="header">
        <h2 v-if="userRole !== 'Teacher'">{{modalInfo.teacher.FirstName}} {{modalInfo.teacher.LastName}}</h2>
        <h3 v-if="selectedComments.length > 0">{{modalInfo.description}}</h3>
        <p v-else>No additional data is available pertaining to this component in this year.</p>
      </div>

        <div class="comments">
          <div v-for="item in selectedComments" :key="item.MOTPID">
            <div class="flex">
              <div>
                <p>DATE</p>
                <p>CLASS</p>
                <p>OBSERVER</p>
              </div>
              <div>
                <p>{{format(item.Date, "MMMM DD, YYYY")}}</p>
                <p>{{item.Class}}</p>
                <p>{{item.Observer}}</p>
              </div>
            </div>

            <div class="flex comment">
              <p>{{item.ComponentRationale}}</p>
              <p class="rating">{{item.Rating.toFixed(2)}}</p>
            </div>
          </div>
        </div>  
      </el-dialog>
      <div 
        class="welcome-note"
        v-if="['Principal', 'Assistant Principal'].indexOf(userRole) !== -1"
      >
        <p>
          <strong>Hi!</strong> Welcome to your observation dashboard!
        </p>
        <ul>
          <li
            v-if="!hasObservations"
          >
              <img src="@/assets/images/announcement.png" alt="announcement">
              <p>We don't have observation data for the 2020-21 school year yet, but you can review your prior year data using the "Year" dropdown below.</p>
          </li>
          <li v-if="userRole === 'Principal' || userRole === 'Assistant Principal'">
            <img src="@/assets/images/assignment.png" alt="assignment">
            <p>The new <router-link :to="{path:'goalSetting/admin'}">Fall Form</router-link>, prompting reflection on how the year is going thus far, is also available for teachers to fill out under the "Professional Goal Setting" tab - you can review any completed response there, too.</p>
          </li>
        </ul>
      </div> 
      <DashboardHeading :userRole="userRole" />

      <div
        v-if="userRole !== 'Teacher'" 
        class="filters" 
      >
        <div class="top-filters">
           <div class="school-filter">
            <span>School</span>
            <FuzzySearchbox
              v-if="schools.length > 1"
              id="select-school"
              v-model="school"
              aria-label="select a school"
              placeholder="Type here to select a school"
              :items="uniqListOfSchools(schools)"
              :pre-selected-item="fetchedSchool"
              :item-keys="['SchoolDBN_Name', 'SchoolDBN']"
              :create-label-function="displayStringForSchool"
            />
            <p v-else>{{ schools[0].display_name }}</p>            
          </div>
          <div class="filter">
            <label>Year</label>
            <select
              v-if="schoolYears" 
              v-model="selectedYear" 
              name="year" 
              id="year"
            >
              <option v-for="year in schoolYears" :key="year" :value="year">{{year-1}} - {{year}}</option>
            </select>
          </div>
        </div>
        <div id="school-dash-filters">
          <div class="filter">
            <ViewFilters :user-role="userRole" />
          </div>
          <div class="filter">
            <label>Evaluator</label>
            <button 
              @click="displayEvaluators = !displayEvaluators"
              class="dropdown-btn"
              :disabled="evaluators.length === 0"
            >
              Select a Evaluator(s)
              <svg class="down-chevron">
                  <line x1="0" y1="3" x2="6" y2="8" stroke="black" stroke-width="2" />
                  <line x1="6" y1="8" x2="12" y2="3" stroke="black" stroke-width="2" />
              </svg>
            </button>
            <div 
              v-if="displayEvaluators"
              class="dropdown"
            >
              <ul @mouseleave="hideEvaluators">
                  <li 
                      v-for="(evaluator, i) in evaluators"
                      :key="`evaluator-${i}`"
                  >
                      <input type="checkbox" :id="`evaluator-${i}`" :value="evaluator" v-model="evalFilters" />
                      <label :for="`evaluator-${i}`">{{ evaluator }}</label>
                  </li>
              </ul>
            </div>
          </div>
          <div class="filter">
            <label>Grade</label>
            <button
              :disabled="grades.length === 0" 
              @click="displayGrades = !displayGrades"
              class="dropdown-btn"
            >
              Select a Grade(s)
              <svg class="down-chevron">
                  <line x1="0" y1="3" x2="6" y2="8" stroke="black" stroke-width="2" />
                  <line x1="6" y1="8" x2="12" y2="3" stroke="black" stroke-width="2" />
              </svg>
            </button>
            <div 
              v-if="displayGrades"
              class="dropdown"
            >
              <ul @mouseleave="hideGrades">
                  <li 
                      v-for="(grade, i) in grades"
                      :key="`grade-${i}`"
                  >
                      <input type="checkbox" :id="`grade-${i}`" :value="grade" v-model="gradeFilters" />
                      <label :for="`grade-${i}`">{{ grade }}</label>
                  </li>
              </ul>
            </div>
          </div>
          <div class="filter">
            <label>Subject</label>
            <button 
              @click="displaySubjects = !displaySubjects"
              class="dropdown-btn"
              :disabled="subjects.length === 0"
            >
              Select a Subject(s)
              <svg class="down-chevron">
                  <line x1="0" y1="3" x2="6" y2="8" stroke="black" stroke-width="2" />
                  <line x1="6" y1="8" x2="12" y2="3" stroke="black" stroke-width="2" />
              </svg>
            </button>
            <div 
              v-if="displaySubjects"
              class="dropdown"
            >
              <ul @mouseleave="hideSubjects">
                  <li 
                      v-for="(subject, i) in subjects"
                      :key="`evaluator-${i}`"
                  >
                      <input type="checkbox" :id="`subject-${i}`" :value="subject" v-model="subjectFilters" />
                      <label :for="`subject-${i}`">{{ subject }}</label>
                  </li>
              </ul>
            </div>
          </div>
          <div class="filter toggle">
            <span 
              :class="['toggle-switch', tipStatus ? 'active' : false]"
            >
              <span @click="tipStatus = !tipStatus" class="toggle-knob"></span>
            </span>
            Teachers with a TIP
          </div>
          <div class="filter">
            <button 
              @click="clearFilters()"
              class="clear-filters-btn"
            >
              <svg width="10" height="10">
                  <line x1="0" y1="0" x2="10" y2="10" stroke-width="2px" stroke="black" />
                  <line x1="0" y1="10" x2="10" y2="0" stroke-width="2px" stroke="black" />
              </svg>
              Clear Filters
            </button>
          </div>
        </div>
      </div>
      <div 
        class="empty-state"
        v-if="!hasObservations"
      >
        <div 
          class="empty-state-content"
        >
          <img src="@/assets/images/error_24px.png" alt="information icon" />
          <div>
            <p>Looks like we don't have any data to display here.</p>
            <p>To start exploring previous data <strong>use the Year dropdown</strong> above.</p>
          </div>
        </div>
      </div>
      <div v-else>
        <div 
          v-if="loading"
          class="loading"
        >
          <p>Loading...</p>
        </div>
        <div v-else>
          <ComponentAveragesTable 
            v-if="listFilteredBySelectedFilters.length > 0"
            :userRole="userRole"
            :teachers="uniqListOfTeachers(listFilteredBySelectedFilters)"
            :fiscalYearSelected="selectedYear" 
          />
          <div 
            v-else
            class="no-data"
          >
            <p>Your selection does not result in any data. Please try a different set of filters.</p>
          </div>
          <!-- Filtered School Averages -->
          <SchoolAverages v-if="userRole !== 'Teacher' && this.areFiltersApplied"
            :school-data-displayed="filteredSchoolAverages"> 
            <th>FILTERED<br>AVERAGE</th>  
          </SchoolAverages>
        
          <!-- Overall (Unfiltered) School Averages -->
          <SchoolAverages v-if="userRole !== 'Teacher' && schoolDataDisplayed"
            :school-data-displayed="schoolDataDisplayed"> 
            <th>SCHOOL<br>AVERAGE</th>
          </SchoolAverages>

          <div class="table-notes">
            <p v-if="selectedYear === 2021">* Please note that observations for the 20-21 school year were added beginning in March 2021.</p>
            <p v-if="selectedYear === 2020">* Please note, teacher evaluations were suspended in mid-March 2020 due to COVID-19 so SY 19-20 MOTP is incomplete.</p>
          </div>

          <div
            class="chart-and-tool flex"
            v-if="listFilteredBySelectedFilters.length > 0"
          >
            <div class="school-level-components">
              <h2>School-Level Component Breakdown</h2>
              <ComponentDistribution :teachers="renderedChartData" />
            </div>
            <SelfReflection :userRole="userRole" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .top-filters {
    display: flex;
  }
  .filters {
    font-family: Arial, sans-serif;
    display: flex;
    flex-direction: column;
    padding: 1em 0;
    margin-bottom: 1em;
    #school-dash-filters {
      margin-top: 2em;
      display: flex;
      align-items: flex-start;
    }
  }
  .filter {
    display: flex;
    flex-direction: column;
    margin-right: 10px;
    button {
      font-size: 1em;
      width: 210px;
      background: transparent;
      border: 1px solid #979797;
      border-radius: 3px;
      padding: 5px 10px;
      text-align: left;
      outline: none;
    }
    select {
      width: 200px;
      padding: 5px;
      font-size: 1em;
      border: 1px solid #979797;
      border-radius: 3px;
    }
  }
  .school-filter {
    width: 300px !important;
    margin-right: 1em;
    span {
      display: block;
      color: #000000;
      font-size: 16px;
    }
    .v-select .vs__selected-options {
      padding: 8px 2px;
    }
  }
  .flex {
    flex-direction: column;
    .school-level-components {
      padding: 1em;
      background-color: #f0f0f0;
      font-family: 'museo_sans', sans-serif;
      margin-bottom: 50px;
      text-align: center;
      h2 {
        margin: 15px auto 25px;
        font-size: 30px;
        text-align: center;
      }
    }
  }
  .dropdown {
    position: relative;
    z-index: 10;
    ul {
        position: absolute;
        width: 200px;
        max-height: 200px;
        overflow: scroll;
        overflow-x: hidden;
        list-style-type: none;
        padding: 5px 20px;
        margin: 5px 0 0 0;
        border: 1px solid #979797;
        background-color: #fff;
        border-radius: 3px;
    }
    li {
        display: flex;
        align-items: center;
        margin-bottom: 5px;
        font-size: .875em;
    }
    li label {
        margin: 0;
    }
  }
  .filter {
    .clear-filters-btn {
      margin-top: 18px;
      display: flex;
      align-items: center;
      border: none;
      cursor: pointer;
      svg {
          margin-right: 10px;
          line {
              stroke: #000 !important;
          }
      }
    }
  }
  .welcome-note {
    font-family: Arial;
    color: #333333;
    padding: 1em;
    border: 2px solid #c4c4c4;
    border-radius: 8px;
    p {
      font-family: Arial;
    }
    > p {
      font-size: 1.25em;  
    }
    ul {
        padding: 0;
    }
    li {
        display: flex;
        align-items: center;
        p {
            font-size: 1.125em;
            font-weight: 400;
            margin: 0 0 0 20px;
        }
    }
    li:first-child {
        margin-bottom: 1em;
    }
  }
  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-size: .9375em;
    p {
      margin: 0;
      line-height: 1.5;
      font-family: Arial;
    }
    .empty-state-content {
      display: flex;
      align-items: flex-start;
      div {
          margin-left: 10px;
      }
    }
    @media all and (min-width: 600px) {
      height: 600px;
    }
  }
  .table-notes {
    margin: 1em 0;
    p {
        margin: 0;
        font-size: 15px;
        font-family: Arial;
    }
  }
  #select-school{
    ::v-deep input.fuzzy-searchbox {
      height: 32px;
      box-sizing: border-box;
      border: 1px solid #979797;
      border-radius: 3px;
      font-size: 16px;
      font-family: Arial;
      box-shadow: none;
    }
    ::v-deep .autocomplete-results {
      li {
        font-size: 14px;
      }
      .is-active {
        background-color: #769D4E;
      }
    }
  }
  .loading {
    width: 100%;
    display: flex;
    justify-content: center;
    align-self: center;
    p {
      font-family: Arial;
    }
    @media all and (min-width: 500px) {
      height: 500px;
    }
  }
  .dropdown-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .filter.toggle {
    display: flex;
    align-items: center;
    flex-direction: row;
    padding-top: 14px;
  }
  .down-chevron {
    width: 12px;
    height: 12px;
  }
  .toggle-switch, .toggle-switch .toggle-knob {
    -moz-transition: all 0.2s ease-in;
    -webkit-transition: all 0.2s ease-in;
    -o-transition: all 0.2s ease-in;
    transition: all 0.2s ease-in;
  }
  .toggle-switch {
    height: 27px;
    width: 48px;
    display: inline-block;
    background-color: #ffffff;
    margin: 2px;
    margin-bottom: 5px;
    border-radius: 30px;
    cursor: pointer;
    border: solid 1px #979797;
    box-shadow: inset 1px 1px 9px -3px rgba(4, 4, 4, 0.08), 1px 2px 6px -2px rgba(0, 0, 0, 0.01);
  }
  .toggle-switch .toggle-knob {
    width: 26px;
    height: 24px;
    display: inline-block;
    background-color: #ffffff;
    border: solid 1px rgba(126, 126, 126, 0.07);
    box-shadow: 0 1px 3px rgba(107, 106, 106, 0.26), 0 5px 1px rgba(107, 106, 106, 0.13);
    border-radius: 26px;
    margin: 1px 1px;
  }
  .toggle-switch.active {
    background-color: #769D4E;
    border: solid 1px transparent;
  }
  .toggle-switch.active .toggle-knob {
      margin-left: 24px;
  }
  .no-data {
    height: 100px;
    text-align: center;
    p {
      font-size: 1.2em;
      font-family: Arial, sans-serif;
    }
  }
</style>